/**
 * The month, as arithmetic. No React, no fetch, no CSS.
 *
 * Three things live here and all three are easy to get subtly wrong in a
 * component:
 *
 *  1. **The grid.** Weeks start on Monday, and the grid is not the month: it
 *     runs from the Monday before the first to the Sunday after the last, so
 *     the leading and trailing cells belong to the neighbouring months. The
 *     window sent to the server therefore covers the GRID, not the month -
 *     otherwise the first and last row would be drawn empty and read as
 *     "nothing is due" on days that have work on them.
 *
 *  2. **Which day an item belongs to.** The server sends instants. The operator
 *     lives in one timezone and the server may live in another, so every
 *     grouping here uses the local day, never `toISOString().slice(0, 10)` -
 *     that is the UTC day, and it moves a 00:30 item to the day before.
 *
 *  3. **Whether an instant has a time on it at all.** Three of the five sources
 *     mean a DAY and store midnight; two carry a time an operator picked. See
 *     `timeLabel`.
 */
import type { CalendarItem } from "../../../lib/calendar";

export interface DayCell {
  date: Date;
  /** Local `YYYY-MM-DD`, the key everything else groups by. */
  key: string;
  /** False for the leading and trailing days borrowed from the months either
   *  side. They are drawn dimmer, and their items still count. */
  inMonth: boolean;
  isToday: boolean;
}

/** Local `YYYY-MM-DD`. Built by hand rather than sliced off an ISO string,
 *  because the ISO string is UTC and this has to be the operator's own day. */
export function dayKey(at: Date): string {
  const m = String(at.getMonth() + 1).padStart(2, "0");
  const d = String(at.getDate()).padStart(2, "0");
  return `${at.getFullYear()}-${m}-${d}`;
}

/** Monday = 0. JS counts from Sunday, and every week in this calendar starts on
 *  Monday, so the shift is here rather than in five places. */
function mondayIndex(at: Date): number {
  return (at.getDay() + 6) % 7;
}

/**
 * The cells a month is drawn with: whole weeks, Monday first, 35 or 42 of them.
 *
 * `month` is 0-based, like `Date`. Keeping the same convention as the platform
 * it wraps means no off-by-one where the two meet.
 */
export function monthGrid(year: number, month: number, today: Date): DayCell[] {
  const first = new Date(year, month, 1);
  const lead = mondayIndex(first);
  // Day 0 of the next month is the last day of this one.
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cellCount = Math.ceil((lead + daysInMonth) / 7) * 7;
  const todayKey = dayKey(today);

  const cells: DayCell[] = [];
  for (let i = 0; i < cellCount; i++) {
    const date = new Date(year, month, 1 - lead + i);
    const key = dayKey(date);
    cells.push({ date, key, inMonth: date.getMonth() === month, isToday: key === todayKey });
  }
  return cells;
}

/**
 * The window to ask the server for: the whole grid, half-open.
 *
 * `to` is the midnight AFTER the last cell, so the last day is included whole
 * and the next month's request does not claim it twice.
 */
export function gridWindow(cells: DayCell[]): { from: string; to: string } {
  const firstCell = cells[0];
  const lastCell = cells[cells.length - 1];
  if (!firstCell || !lastCell) throw new Error("empty grid");
  const from = firstCell.date;
  const to = new Date(lastCell.date.getFullYear(), lastCell.date.getMonth(), lastCell.date.getDate() + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * The clock time to print on a chip, or null when there is none worth printing.
 *
 * All five source columns are timestamps, not dates, and two of the five carry
 * a time somebody actually chose: `Activity.dueAt` comes from a
 * `datetime-local` field, and `Task.dueDate` is routinely set to an afternoon.
 * The other three are dates in everything but storage - an invoice is due ON a
 * day, not at a minute - and they land on local midnight.
 *
 * So midnight returns null rather than "00:00". Printing it would put a clock
 * time on every invoice in the month, which is noise, and it would read as a
 * deadline at midnight, which is a claim nobody made. A day with a 09:00 and a
 * 16:00 on it is the case this exists for: those two are ordered correctly
 * already, and until now they were indistinguishable.
 */
export function timeLabel(at: Date, locale: string): string | null {
  if (at.getHours() === 0 && at.getMinutes() === 0) return null;
  try {
    return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(at);
  } catch {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(at.getHours())}:${pad(at.getMinutes())}`;
  }
}

/**
 * Items by the local day they fall on, earliest first within each day.
 *
 * `loadCalendar` already sorts the merged list, so this sort is usually a no-op
 * - and it stays anyway. The moment the chips started showing the clock, an
 * out-of-order day stopped being invisible and became a screen that reads
 * 14:30, 10:00, 11:00 down a cell. Trusting a sort that happened on the far side
 * of a network boundary is exactly the kind of assumption that holds until
 * somebody adds a sixth source and appends it after the sort.
 *
 * Sorting on the instant rather than on the string, because the strings only
 * compare correctly while every one of them is UTC with the same number of
 * fractional digits - true of `toISOString` today, and not a property of the
 * field's type.
 */
export function groupByDay(items: CalendarItem[]): Map<string, CalendarItem[]> {
  const out = new Map<string, CalendarItem[]>();
  for (const item of items) {
    const key = dayKey(new Date(item.at));
    const list = out.get(key);
    if (list) list.push(item);
    else out.set(key, [item]);
  }
  // Stable, so two things due at the same minute keep the order the server put
  // them in rather than swapping about between renders.
  for (const list of out.values()) {
    list.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  }
  return out;
}

/** Step one month, wrapping the year. */
export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const at = new Date(year, month + delta, 1);
  return { year: at.getFullYear(), month: at.getMonth() };
}

/** "august 2026" in the interface language, capitalised by the caller if it
 *  wants. Falls back to a plain number pair where the locale is unknown. */
export function monthLabel(year: number, month: number, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(new Date(year, month, 1));
  } catch {
    return `${month + 1}.${year}`;
  }
}

/**
 * The twelve month names, January first, in the interface language.
 *
 * Short form, because they are read three across in a grid where the longest
 * Estonian month ("september") would set the column width for all twelve. Built
 * against a fixed year for the same reason `weekdayLabels` uses a fixed Monday:
 * the answer must not depend on what day the function is called.
 */
export function monthNames(locale: string): string[] {
  return Array.from({ length: 12 }, (_, m) => {
    try {
      return new Intl.DateTimeFormat(locale, { month: "short" }).format(new Date(2026, m, 1));
    } catch {
      return String(m + 1);
    }
  });
}

/** The seven weekday heads, Monday first, in the interface language. */
export function weekdayLabels(locale: string): string[] {
  // 2026-01-05 is a Monday. Any Monday works; a fixed one keeps this pure.
  const monday = new Date(2026, 0, 5);
  const out: string[] = [];
  for (let i = 0; i < 7; i++) {
    const at = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
    try {
      out.push(new Intl.DateTimeFormat(locale, { weekday: "short" }).format(at));
    } catch {
      out.push(String(i + 1));
    }
  }
  return out;
}
